import React, { useState } from "react";
import axios from "axios";
import { useApp } from "@/context/AppContext";
import ChatPanel from "@/components/ChatPanel";
import { GraduationCap, MessageSquare, ArrowRight, Download, CheckCircle2, FileText } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { externalLinkHandler, externalTarget } from "@/utils/openExternal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WHATSAPP_URL =
  "https://wa.me/34669024616?text=" +
  encodeURIComponent(
    "Hola, quiero apuntarme a la lista de espera del Curso Manitas de la Academia Homeatope."
  );

export default function Academy() {
  const { t, lang } = useApp();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleDownload = async (e) => {
    e.preventDefault();
    if (sending) return;
    setError("");
    setSending(true);
    try {
      const res = await axios.post(`${API}/waitlist`, { email, language: lang });
      if (res.data?.ok) {
        setDone(true);
        // Trigger PDF download
        const link = document.createElement("a");
        link.href = res.data.pdf_url || "/curso-manitas-temario.pdf";
        link.download = "curso-manitas-temario.pdf";
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      setError(t.academy.pdfError);
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="academia"
      data-testid="academy-section"
      className="relative ht-section border-t"
      style={{ borderColor: "var(--ht-border)" }}
    >
      {/* Accent backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-25"
        style={{
          background:
            "radial-gradient(800px 400px at 80% 0%, rgba(var(--ht-accent-rgb),0.16), transparent 60%), radial-gradient(700px 350px at 0% 100%, rgba(var(--ht-primary-rgb),0.16), transparent 60%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Course cover hero */}
        <div
          className="ht-card relative overflow-hidden rounded-2xl px-6 py-10 sm:px-10 sm:py-12"
          style={{
            background:
              "linear-gradient(140deg, #0E2A4A 0%, #143C66 55%, #0E2A4A 100%)",
            borderColor: "transparent",
          }}
          data-testid="academy-cover"
        >
          {/* Subtle grid pattern */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
              backgroundSize: "44px 44px",
            }}
          />
          {/* Corner "Próximamente" ribbon */}
          <div
            className="pointer-events-none absolute right-0 top-0 z-10"
            data-testid="academy-coming-soon-badge"
          >
            <div
              className="flex items-center gap-2 rounded-bl-2xl rounded-tr-2xl px-4 py-2 text-xs font-bold uppercase tracking-[0.18em]"
              style={{
                background: "linear-gradient(135deg, #F58A2C, #E32726)",
                color: "#fff",
                boxShadow: "0 8px 24px -8px rgba(227,39,38,0.6)",
              }}
            >
              <span
                className="ht-pulse inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: "#fff" }}
                aria-hidden
              />
              {t.academy.comingSoonBadge}
            </div>
          </div>

          {/* Pill: HOME a tope */}
          <div className="relative inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-4 py-2 backdrop-blur">
            <span className="ht-wordmark" translate="no" style={{ lineHeight: 0.9 }}>
              <span
                className="ht-wordmark-home text-sm"
                translate="no"
                style={{ color: "#fff" }}
              >
                HOME
              </span>
              <span
                className="ht-wordmark-atope text-xs"
                translate="no"
                style={{ color: "#F58A2C" }}
              >
                a tope
              </span>
            </span>
          </div>

          <p
            className="relative mt-6 text-xs font-semibold uppercase tracking-[0.22em]"
            style={{ color: "rgba(255,255,255,0.55)" }}
            data-testid="academy-eyebrow"
          >
            <GraduationCap size={12} className="-mt-0.5 mr-1 inline" />
            {t.academy.eyebrow}
          </p>

          {/* Big course title — inspired by the cover */}
          <div className="relative mt-3 flex flex-col gap-0 sm:flex-row sm:items-end sm:gap-8">
            <span
              translate="no"
              className="block leading-[0.82] tracking-[-0.02em]"
              style={{
                fontFamily: "Anton, 'Space Grotesk', sans-serif",
                fontSize: "clamp(3.5rem, 9vw, 7rem)",
                color: "#fff",
                textTransform: "uppercase",
              }}
              data-testid="academy-title"
            >
              CURSO
              <br />
              <em
                style={{
                  color: "#F58A2C",
                  fontStyle: "italic",
                  letterSpacing: "0.01em",
                }}
              >
                MANITAS
              </em>
            </span>
          </div>

          {/* Quote */}
          <blockquote
            className="relative mt-8 max-w-2xl border-l-4 pl-5 text-base italic sm:text-lg"
            style={{
              borderColor: "#F58A2C",
              color: "rgba(255,255,255,0.92)",
              fontFamily: "Inter, sans-serif",
            }}
            data-testid="academy-tagline"
          >
            {t.academy.tagline}
          </blockquote>

          <p
            className="relative mt-5 max-w-2xl text-sm sm:text-base"
            style={{ color: "rgba(255,255,255,0.7)" }}
            data-testid="academy-subtitle"
          >
            {t.academy.subtitle}
          </p>

          {/* Coming soon notice */}
          <div
            className="relative mt-6 flex items-start gap-3 rounded-xl border p-4 max-w-2xl"
            style={{
              borderColor: "rgba(245,138,44,0.4)",
              backgroundColor: "rgba(245,138,44,0.08)",
            }}
            data-testid="academy-coming-soon-notice"
          >
            <span
              className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold"
              style={{ backgroundColor: "#F58A2C", color: "#fff" }}
              aria-hidden
            >
              !
            </span>
            <p
              className="text-sm leading-relaxed"
              style={{ color: "rgba(255,255,255,0.9)" }}
            >
              <span className="font-semibold" style={{ color: "#F58A2C" }}>
                {t.academy.comingSoonBadge}.
              </span>{" "}
              {t.academy.comingSoonNotice}
            </p>
          </div>

          <div className="relative mt-7 flex flex-wrap gap-3">
            <a
              href={WHATSAPP_URL}
              target={externalTarget()}
              rel="noopener noreferrer"
              onClick={externalLinkHandler(WHATSAPP_URL)}
              data-testid="academy-cta-primary"
              className="inline-flex items-center gap-2 rounded-md px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] transition-transform hover:translate-y-[-1px]"
              style={{
                backgroundColor: "#F58A2C",
                color: "#fff",
                boxShadow: "0 12px 28px -10px rgba(245, 138, 44, 0.55)",
              }}
            >
              {t.academy.ctaPrimary}
              <ArrowRight size={16} />
            </a>
            <a
              href="#chat-panel-anchor"
              data-testid="academy-cta-secondary"
              className="inline-flex items-center gap-2 rounded-md border border-white/30 px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors hover:bg-white/10"
            >
              <MessageSquare size={16} />
              {t.academy.ctaSecondary}
            </a>
          </div>
        </div>

        {/* Temario + PDF download block */}
        <div className="mt-12 grid gap-8 lg:grid-cols-12">
          {/* Accordion with 6 blocks */}
          <div className="lg:col-span-7">
            <div className="ht-card p-6 sm:p-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-xl font-semibold sm:text-2xl" style={{ color: "var(--ht-text)" }} data-testid="curriculum-title">
                    {t.academy.curriculumTitle}
                  </h3>
                  <p className="mt-1 text-sm" style={{ color: "var(--ht-text-muted)" }}>
                    {t.academy.curriculumSubtitle}
                  </p>
                </div>
                <FileText size={22} style={{ color: "var(--ht-accent)" }} />
              </div>
              <Accordion type="single" collapsible defaultValue="b1" className="mt-4" data-testid="curriculum-accordion">
                {t.academy.modules.map((mod) => (
                  <AccordionItem key={mod.key} value={mod.key} data-testid={`module-item-${mod.key}`} style={{ borderColor: "var(--ht-border)" }}>
                    <AccordionTrigger className="text-left font-medium hover:no-underline" style={{ color: "var(--ht-text)" }}>
                      {mod.title}
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-2 pl-1">
                        {mod.items.map((it, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: "var(--ht-text-muted)" }}>
                            <CheckCircle2 size={16} className="mt-0.5 shrink-0" style={{ color: "var(--ht-accent)" }} />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>

          {/* PDF download card */}
          <div className="lg:col-span-5">
            <div
              className="ht-card relative flex h-full flex-col justify-between overflow-hidden p-6 sm:p-8"
              style={{ background: "linear-gradient(140deg, #0E2A4A 0%, #143C66 100%)", borderColor: "transparent" }}
              data-testid="pdf-download-card"
            >
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: "#F58A2C" }}>
                  <Download size={12} />
                  Lead magnet · PDF
                </div>
                <h3 className="mt-4 text-2xl font-bold sm:text-3xl" style={{ color: "#fff", fontFamily: "Space Grotesk, sans-serif" }} data-testid="pdf-title">
                  {t.academy.pdfTitle}
                </h3>
                <p className="mt-3 text-sm sm:text-base" style={{ color: "rgba(255,255,255,0.75)" }}>
                  {t.academy.pdfSubtitle}
                </p>
              </div>

              <form onSubmit={handleDownload} className="mt-6 flex flex-col gap-3" data-testid="pdf-form">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.academy.pdfEmailPlaceholder}
                  data-testid="pdf-email-input"
                  className="w-full rounded-md border px-4 py-3 text-sm outline-none transition-colors"
                  style={{ backgroundColor: "rgba(255,255,255,0.08)", color: "#fff", borderColor: "rgba(255,255,255,0.2)" }}
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="pdf-submit-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-md px-6 py-3 text-sm font-semibold uppercase tracking-[0.14em] transition-transform hover:translate-y-[-1px] disabled:opacity-60"
                  style={{ backgroundColor: "#F58A2C", color: "#fff", boxShadow: "0 12px 28px -10px rgba(245,138,44,0.55)" }}
                >
                  <Download size={16} />
                  {sending ? t.academy.pdfSending : t.academy.pdfCta}
                </button>
                {done && (
                  <div className="flex items-start gap-2 rounded-md px-3 py-2 text-xs" style={{ backgroundColor: "rgba(34,197,94,0.15)", color: "#86efac" }} data-testid="pdf-success">
                    <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
                    <span>{t.academy.pdfSuccess}</span>
                  </div>
                )}
                {error && (
                  <div className="rounded-md px-3 py-2 text-xs" style={{ backgroundColor: "rgba(239,68,68,0.15)", color: "#fca5a5" }}>
                    {error}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-12" id="chat-panel-anchor">
          {/* Photo of Gerardo, complementing "Habla con Gerardo IA" */}
          <div className="lg:col-span-5">
            <div className="relative h-full">
              <img
                src="https://customer-assets.emergentagent.com/job_homeatope-academy/artifacts/pwitswvg_WhatsApp%20Image%202026-05-19%20at%2017.57.24.jpeg"
                alt="Gerardo Salvo · Home a tope"
                data-testid="academy-photo"
                className="ht-card h-full w-full rounded-2xl object-cover"
                style={{ minHeight: "520px", maxHeight: "620px", objectPosition: "center" }}
                loading="lazy"
              />
              <div
                className="ht-glass absolute bottom-4 left-4 right-4 flex items-start gap-3 rounded-xl px-4 py-3"
                data-testid="academy-photo-caption"
              >
                <span
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundColor: "var(--ht-accent)", color: "#fff" }}
                >
                  <MessageSquare size={16} />
                </span>
                <div className="flex flex-col leading-tight">
                  <span
                    className="text-sm font-semibold"
                    style={{ color: "var(--ht-text)" }}
                  >
                    Gerardo, en persona y también en IA
                  </span>
                  <span
                    className="text-xs"
                    style={{ color: "var(--ht-text-muted)" }}
                  >
                    Habla con su asistente aquí al lado
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Chat */}
          <div className="lg:col-span-7">
            <ChatPanel />
          </div>
        </div>
      </div>
    </section>
  );
}
